import type { PuntosEntregaRepositoryPort } from '../domain/ports/PuntosEntregaRepositoryPort.ts';
import type { ParticipantePuntosEntregaBusquedaPrefill } from '../domain/puntoEntregaModel.ts';
import { participanteBusquedaFailureMensaje } from '../../shared/participante/domain/participanteBusquedaFailureMensaje.ts';
import { participanteComunaRegistroAviso } from '../../shared/participante/domain/participanteComunaRegistroAviso.ts';
import { PUNTOS_ENTREGA_PARTICIPANTE_BUSQUEDA as PE_PARTICIPANTE_BUSQUEDA } from '../domain/puntosEntregaParticipantBusquedaCopy.ts';

export type ParticipanteBusquedaPeFormResult =
  | {
      ok: false;
      docMensaje: string;
      participanteNombre: string;
      participanteId: null;
      pepTelefono: string;
      pepCelular: string;
    }
  | {
      ok: true;
      docMensaje: string;
      participanteNombre: string;
      participanteId: number;
      pepTelefono: string;
      pepCelular: string;
      politicaTratamientos: boolean;
      bonoId: number;
      bonoFieldText: string;
      tipoBono: string;
      comunaRegistroAviso: string;
    };

const emptyPeBusquedaFailure = (): Extract<ParticipanteBusquedaPeFormResult, { ok: false }> => ({
  ok: false,
  docMensaje: '',
  participanteNombre: '',
  participanteId: null,
  pepTelefono: '',
  pepCelular: '',
});

function buildPeFormSuccessFromPrefill(
  pre: ParticipantePuntosEntregaBusquedaPrefill,
  puntoEntregaComunaId: number,
): Extract<ParticipanteBusquedaPeFormResult, { ok: true }> {
  const nombre = pre.nombreParticipante.trim() || pre.resumen.nombre;
  const bonoNombre = pre.bonoNombre?.trim() ?? '';
  const rawBonoId = pre.bonoId != null && pre.bonoId > 0 ? pre.bonoId : 0;
  const bonoFieldText = bonoNombre.length > 0 ? bonoNombre : rawBonoId > 0 ? String(rawBonoId) : '';
  return {
    ok: true,
    docMensaje: '',
    participanteNombre: nombre,
    participanteId: pre.resumen.id,
    pepTelefono: pre.telefono,
    pepCelular: pre.celular,
    politicaTratamientos: pre.politicaTratamientos,
    bonoId: rawBonoId,
    bonoFieldText,
    tipoBono: pre.tipoBono ?? '',
    comunaRegistroAviso: participanteComunaRegistroAviso(puntoEntregaComunaId, pre.resumen),
  };
}

export function findParticipanteBusquedaForPeForm(
  repo: PuntosEntregaRepositoryPort,
  docBusqueda: string,
  puntoEntregaComunaId: number,
): ParticipanteBusquedaPeFormResult {
  const docTrim = docBusqueda.trim();
  if (!docTrim) return emptyPeBusquedaFailure();

  const resolved = repo.resolveParticipantePuntosEntregaBusquedaPrefill(docTrim);
  if (!resolved.ok) {
    return {
      ...emptyPeBusquedaFailure(),
      docMensaje:
        resolved.reason === 'empty'
          ? ''
          : participanteBusquedaFailureMensaje(resolved.reason, PE_PARTICIPANTE_BUSQUEDA),
    };
  }
  return buildPeFormSuccessFromPrefill(resolved.prefill, puntoEntregaComunaId);
}

export function findParticipanteBusquedaForPeFormByParticipanteId(
  repo: PuntosEntregaRepositoryPort,
  participanteId: number,
  puntoEntregaComunaId: number,
): ParticipanteBusquedaPeFormResult {
  if (!Number.isFinite(participanteId) || participanteId <= 0) return emptyPeBusquedaFailure();
  const resolved = repo.resolveParticipantePuntosEntregaBusquedaPrefillByParticipanteId(participanteId);
  if (!resolved.ok) {
    return {
      ...emptyPeBusquedaFailure(),
      docMensaje: participanteBusquedaFailureMensaje('not_found', PE_PARTICIPANTE_BUSQUEDA),
    };
  }
  return buildPeFormSuccessFromPrefill(resolved.prefill, puntoEntregaComunaId);
}
